import { useFormContext } from 'react-hook-form';
import { CurrencySelector, FormField } from '@percent/lemonade';
import { CurrencyCode, currencyCodes } from '@percent/utility';
import get from 'lodash/get';
import { CurrencyOnlyQuestion as CurrencyOnlyQuestionType } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { DEFAULT_OPERATING_BUDGET_CURRENCY } from '@percent/grants-nomination-form/constants/operatingBudget';
import { formatKey } from '@percent/grants-nomination-form/utils/formKey';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ALL_CURRENCIES: CurrencyCode[] = [...currencyCodes];

type Props = { question: CurrencyOnlyQuestionType };

export const CurrencyOnlyQuestion = ({ question }: Props) => {
  const { t } = useTranslation();
  const { setValue, getValues, formState } = useFormContext();

  const currencies = question.allowedCurrencies?.length ? question.allowedCurrencies : ALL_CURRENCIES;
  const defaultCurrency = question.defaultCurrency ?? DEFAULT_OPERATING_BUDGET_CURRENCY;

  const currentValue = getValues(question.key) as CurrencyCode | undefined;
  const initial = currentValue ?? defaultCurrency;

  useEffect(() => {
    if (currentValue === undefined) {
      setValue(question.key, defaultCurrency, { shouldValidate: true, shouldTouch: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedKey = formatKey(question.key);
  const touched = get(formState.touchedFields, formattedKey);
  const error = touched ? get(formState.errors, formattedKey) : undefined;

  return (
    <FormField
      label={question.label}
      status={error ? 'danger' : 'default'}
      necessity={question.required ? 'required' : 'optional'}
      optionalText={t('typography.optional')}
      statusMessage={error ? (error as { message?: string }).message?.toString() : undefined}
      description={question.description}
      instructions={question.instructions}
      data-testid={question.key}
    >
      <CurrencySelector
        defaultCurrency={initial}
        currencies={currencies}
        onValueChange={(v) => {
          if (v) setValue(question.key, v, { shouldValidate: true, shouldTouch: true });
        }}
      />
    </FormField>
  );
};
